import { Box, Button, Flex, HStack, Image, Select, Spinner, Table, TableContainer, Tbody, Td, Text, Th, Thead, Tooltip, Tr, IconButton, Checkbox } from "@chakra-ui/react";
import { AddIcon, ChevronLeftIcon, ChevronRightIcon, ChevronUpIcon, EditIcon, DeleteIcon, DownloadIcon, RepeatIcon } from "@chakra-ui/icons";
import { FiFilter } from "react-icons/fi";
import { useState, useEffect, useRef } from "react";
import SearchBox from "./SearchBox";
import { useNavigate } from "react-router-dom";
import { getImageURL } from "../util/getImage";
import FilterPills from "./Filter/FilterPills";
import SortBy from "./Filter/SortBy";
import { common, userProfile, siteInfo } from "../constants/constants";
const IMAGE_BASE_PATH = process.env.REACT_APP_DEV_IMAGE_BASE_PATH;
const placeholderImg = `${IMAGE_BASE_PATH}/ui/DummyImage/small.png`

const TableStructure = ({ isAuthenticated, disableRowClick, handleSearch, handleClear, handlePageChange, currentPage, totalPages, sortRecords, sortOrder, loading, records, type, title, columns = [], addOption = false, pagination = true, topPagination = false, totalRecords = 0, filterPills, setFilterOptions, setApply, setSearchTerm, setFilterPills, filteredResultCount, filterCount, filterAvailble, handleCheckboxChange, sort, filterOptions, explore = false, selectable = false, selectedRowKeys = [], getRowKey, onRowSelect, onSelectAll, onRowDelete, onDeleteSelected, exportHandler, onRowExport, hideActions = false, onRefresh, sortOptions, selectedSort, onSortChange, filterPanel = null, onFilterToggle, showFilter, ...props }) => {
  const navigate = useNavigate();
  const tableContainerRef = useRef(null);
  const [showScrollTop, setShowScrollTop] = useState(false);

  // Global scroll to top functionality
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setShowScrollTop(scrollTop > 200);
    };

    window.addEventListener('scroll', handleScroll);
    // Initial check
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const computeRowKey = (record) => {
    if (getRowKey) return getRowKey(record);
    return record?._id || record?.id || record?.alias;
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return "";
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return isoString;
    try {
      return date.toLocaleString('en-IN', {
        timeZone: siteInfo?.INDIANTIMEZONE || 'Asia/Kolkata',
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (e) {
      return date.toLocaleString();
    }
  };

  const allRowKeys = Array.isArray(records) ? records?.map((r) => computeRowKey(r)).filter(Boolean) : [];
  const allSelected = selectable && selectedRowKeys && allRowKeys.length > 0 && allRowKeys.every((k) => selectedRowKeys.includes(k));
  const isIndeterminate = selectable && selectedRowKeys && selectedRowKeys.length > 0 && !allSelected;

  const createNewRecord = () => {
    const url = window.location.pathname;
    navigate(url + "/create") // this will navigate to the create page by appending /create to the current URL
  }

  const computeHref = (record) => {
    if (explore) {
      return `${record.navigationLink}`;
    }
    return `/${type}/${type === 'static-words' ? record?.key : (record?.alias || record?.masterlist_alias || record?._id || record?.id) || record?.slug}`;
  };

  const handleEdit = (record) => {
    // Navigate to edit page
    navigate(computeHref(record));
  };

  return (
    <>
      {isAuthenticated ? (
        <Box
          px={"20px"}
          pt={"5px"}
          pb={"50px"}
          width="100%"
          mx="auto"
          {...props}
          padding={"20px"}
          bg={"white"}
          height="calc(100vh - 120px)"
          display="flex"
          flexDirection="column"
          overflow="hidden"
        >
          <Box
            position={"sticky"}
            zIndex={99}
            top={"0px"}
            bg="white"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            py={1}
            px={2}
            boxShadow="sm"
            border="1px solid"
            borderColor="gray.100"
            flexShrink={0}
          >
            <Flex alignItems="center" gap={4}>
              <Text
                fontSize="24px"
                color="gray.800"
                fontWeight="700"
                width={"max-content"}
                letterSpacing="-0.5px"
              >
                {title}
              </Text>
              {totalRecords > 0 && (
                <Box
                  display="inline-flex"
                  alignItems="center"
                  bg="blue.50"
                  color="blue.700"
                  fontSize="md"
                  width={"max-content"}
                  fontWeight="600"
                  px={3}
                  py={1.5}
                  borderRadius="full"
                  border="1px solid"
                  borderColor="blue.200"
                  boxShadow="sm"
                  _hover={{
                    bg: "blue.100",
                    borderColor: "blue.300",
                    transform: "translateY(-1px)"
                  }}
                  transition="all 0.2s ease"
                >
                  {/* <Text as="span" fontSize="15px" color="blue.600" mr={1}>
                      •
                    </Text> */}
                  <Text width={"max-content"}>{totalRecords.toLocaleString()} {totalRecords === 1 ? 'Record' : 'Records'}</Text>
                </Box>
              )}
              {addOption && (
                <Button
                  variant="solid"
                  size="md"
                  onClick={createNewRecord}
                  bg="blue.600"
                  color="white"
                  px={4}
                  py={2}
                  borderRadius="lg"
                  fontWeight="600"
                  fontSize="14px"
                  lineHeight={"16px"}
                  leftIcon={<AddIcon boxSize={3} />}
                  _hover={{
                    bg: "blue.700",
                    transform: "translateY(-1px)",
                    boxShadow: "md"
                  }}
                  _active={{
                    bg: "blue.800",
                    transform: "translateY(0)"
                  }}
                  transition="all 0.2s ease"
                  boxShadow="sm"
                >
                  {common.ADD_NEW_RECORD}
                </Button>
              )}
              {selectable && onDeleteSelected && selectedRowKeys && selectedRowKeys.length > 0 && (
                <Button
                  variant="solid"
                  size="md"
                  ml={2}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteSelected();
                  }}
                  bg="red.600"
                  color="white"
                  px={4}
                  py={2}
                  borderRadius="lg"
                  fontWeight="600"
                  fontSize="14px"
                  lineHeight={"16px"}
                  leftIcon={<DeleteIcon boxSize={3} />}
                  _hover={{
                    bg: "red.700",
                    transform: "translateY(-1px)",
                    boxShadow: "md"
                  }}
                  _active={{
                    bg: "red.800",
                    transform: "translateY(0)"
                  }}
                  transition="all 0.2s ease"
                  boxShadow="sm"
                >
                  Delete Selected ({selectedRowKeys.length})
                </Button>
              )}
            </Flex>
            <Flex flexWrap={"wrap"} justifyContent={"flex-end"} alignItems={"baseline"} gap={"10px"}>
              {
                onRefresh && (<Box>
                  <Button leftIcon={<RepeatIcon />} onClick={onRefresh} colorScheme="blue">
                    {common.REFRESH}
                  </Button>
                </Box>)
              }

              <Box display="flex" gap={2} flexShrink={0}>
                <SearchBox onSearch={handleSearch} onClear={handleClear} type={type} />
              </Box>

              {onFilterToggle && (
                <Button
                  size="sm"
                  fontSize="14px"
                  fontWeight="600"
                  onClick={onFilterToggle}
                  leftIcon={<FiFilter size={16} />}
                  variant={showFilter ? "solid" : "outline"}
                  borderColor="blue.300"
                  color={showFilter ? "white" : "blue.600"}
                  bg={showFilter ? "blue.600" : "white"}
                  _hover={{
                    bg: showFilter ? "blue.700" : "blue.50",
                    borderColor: "blue.400",
                    color: showFilter ? "white" : "blue.700",
                    transform: "translateY(-1px)",
                    boxShadow: "sm"
                  }}
                  _active={{
                    transform: "translateY(0)",
                    bg: showFilter ? "blue.800" : "blue.100"
                  }}
                  transition="all 0.2s ease"
                  borderRadius="lg"
                  px={4}
                  py={2}
                  height="40px"
                  flexShrink={0}
                >
                  {common.FILTER}
                </Button>
              )}

              {(topPagination || exportHandler) && (
                <Flex
                  alignItems="center"
                  gap={3}
                  bg="white"
                  p={1.5}
                  borderRadius="xl"
                  border="1px solid"
                  borderColor="gray.200"
                  boxShadow="sm"
                  flexShrink={0}
                >
                  {topPagination && (
                    <>
                      <Button
                        size="sm"
                        fontSize="14px"
                        fontWeight="600"
                        onClick={() => handlePageChange(Number(currentPage - 1))}
                        isDisabled={currentPage === 1}
                        variant="outline"
                        borderColor="gray.300"
                        color="gray.600"
                        bg="white"
                        leftIcon={<ChevronLeftIcon boxSize={4} />}
                        _hover={{
                          bg: "gray.50",
                          borderColor: "gray.400",
                          color: "gray.700",
                          transform: "translateY(-1px)",
                          boxShadow: "sm"
                        }}
                        _active={{
                          transform: "translateY(0)",
                          bg: "gray.100"
                        }}
                        _disabled={{
                          opacity: 0.5,
                          cursor: "not-allowed",
                          _hover: {
                            transform: "none",
                            boxShadow: "none",
                            bg: "white"
                          }
                        }}
                        transition="all 0.2s ease"
                        borderRadius="lg"
                        px={4}
                        py={2}
                        height="36px"
                      >
                        {common.PREVIOUS}
                      </Button>

                      <Box
                        display="flex"
                        alignItems="center"
                        bg="gray.50"
                        borderRadius="lg"
                        border="1px solid"
                        borderColor="gray.200"
                        px={3}
                        py={1}
                        minW="120px"
                        justifyContent="center"
                      >
                        <Text fontSize="13px" fontWeight="600" color="gray.600" mr={2}>
                          {common.PAGE}
                        </Text>
                        <Select
                          size="sm"
                          width="60px"
                          value={currentPage}
                          onChange={(e) => handlePageChange(Number(e.target.value))}
                          variant="unstyled"
                          fontSize="14px"
                          fontWeight="700"
                          color="blue.600"
                          textAlign="center"
                          cursor="pointer"
                          _hover={{
                            color: "blue.700"
                          }}
                          _focus={{
                            boxShadow: "none"
                          }}
                        >
                          {totalPages && Array.from({ length: totalPages }, (_, i) => (
                            <option key={i + 1} value={i + 1}>
                              {i + 1}
                            </option>
                          ))}
                        </Select>
                        <Text fontSize="13px" fontWeight="500" color="gray.500" ml={1}>
                          of {totalPages}
                        </Text>
                      </Box>

                      <Button
                        size="sm"
                        fontSize="14px"
                        fontWeight="600"
                        onClick={() => handlePageChange(Number(currentPage + 1))}
                        isDisabled={currentPage === totalPages}
                        variant="outline"
                        borderColor="gray.300"
                        color="gray.600"
                        bg="white"
                        rightIcon={<ChevronRightIcon boxSize={4} />}
                        _hover={{
                          bg: "gray.50",
                          borderColor: "gray.400",
                          color: "gray.700",
                          transform: "translateY(-1px)",
                          boxShadow: "sm"
                        }}
                        _active={{
                          transform: "translateY(0)",
                          bg: "gray.100"
                        }}
                        _disabled={{
                          opacity: 0.5,
                          cursor: "not-allowed",
                          _hover: {
                            transform: "none",
                            boxShadow: "none",
                            bg: "white"
                          }
                        }}
                        transition="all 0.2s ease"
                        borderRadius="lg"
                        px={4}
                        py={2}
                        height="36px"
                      >
                        {common.NEXT}
                      </Button>
                    </>
                  )}

                  {/* Export Button */}
                  {exportHandler && (
                    <Button
                      size="sm"
                      fontSize="14px"
                      fontWeight="600"
                      onClick={exportHandler}
                      variant="outline"
                      borderColor="blue.300"
                      color="blue.600"
                      bg="white"
                      ml={topPagination ? 2 : 0}
                      _hover={{
                        bg: "blue.50",
                        borderColor: "blue.400",
                        color: "blue.700",
                        transform: "translateY(-1px)",
                        boxShadow: "sm"
                      }}
                      _active={{
                        transform: "translateY(0)",
                        bg: "blue.100"
                      }}
                      transition="all 0.2s ease"
                      borderRadius="lg"
                      px={4}
                      py={2}
                      height="36px"
                    >
                      {common.EXPORT}
                    </Button>
                  )}
                </Flex>
              )}
              <HStack mb="10px" justifyContent={"flex-end"}>
                {sort && sort?.length > 0 && records && records?.length > 0 && filterOptions &&
                  <SortBy
                    sort={sort}
                    filterOptions={filterOptions}
                    handleCheckboxChange={handleCheckboxChange}
                    setApply={setApply}
                  />
                }
                {sortOptions && sortOptions.length > 0 && (
                  <Select
                    size="sm"
                    width="200px"
                    value={selectedSort || ""}
                    onChange={(e) => onSortChange && onSortChange(e.target.value)}
                    borderRadius="lg"
                    borderColor="gray.300"
                    fontSize="13px"
                    fontWeight="500"
                    _focus={{ borderColor: "blue.400", boxShadow: "0 0 0 1px #63b3ed" }}
                  >
                    {sortOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </Select>
                )}
                {sortRecords && (
                  <Box display="flex" justifyContent="flex-end">
                    <Tooltip label="Sort Titles">
                      <Button
                        size="md"
                        onClick={sortRecords}
                        variant="outline"
                        _hover={{ bg: "#e8e8e8" }}
                      >
                        <Text fontSize="22px">{sortOrder === "asc" ? "↓" : "↑"}</Text>
                      </Button>
                    </Tooltip>
                  </Box>
                )}
              </HStack>
            </Flex>
          </Box>
          {((filterPills && filterPills?.search) || filterCount > 0) && <Box position={"sticky"} top={"53px"} bg="white" zIndex={9} width={"100%"}>
            {((filterPills && filterPills?.search !== '') || filterCount > 0) &&
              <FilterPills
                filters={filterPills}
                setFilterOptions={setFilterOptions}
                setApply={setApply}
                setSearchTerm={setSearchTerm}
                setFilterPills={setFilterPills}
                filteredResultCount={filteredResultCount}
              />
            }
          </Box>}

          {/* Table */}
          <HStack flex="1" alignItems="stretch" spacing={0} overflow="hidden" minH="0" mt={4}>
          <Box
            bg="white"
            border="1px solid"
            borderColor="gray.200"
            boxShadow="sm"
            position="relative"
            flex="1"
            minH="0"
            display="flex"
            flexDirection="column"
          >
            <TableContainer
              ref={tableContainerRef}
              overflowY="auto"
              overflowX="hidden"
              flex="1"
              width="100%"
              pb="20px"
              css={{
                '&::-webkit-scrollbar': {
                  width: '3px',
                },
                '&::-webkit-scrollbar-track': {
                  background: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#e2e8f0',
                  borderRadius: '2px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  background: '#cbd5e0',
                },
                // Firefox scrollbar styling
                scrollbarWidth: 'thin',
                scrollbarColor: '#e2e8f0 transparent',
              }}
            >
              <Table variant="simple" width="100%" size="sm">
                <Thead
                  bg="linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)"
                  position="sticky"
                  top="0"
                  zIndex={10}
                  _before={{
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    bg: "linear-gradient(90deg, rgba(59, 130, 246, 0.05) 0%, rgba(147, 51, 234, 0.05) 100%)",
                    pointerEvents: "none"
                  }}
                >
                  <Tr>
                    {columns?.length > 0 ? (
                      <>
                        {selectable && (
                          <Th
                            fontSize="13px"
                            fontWeight="800"
                            color="gray.800"
                            textTransform="uppercase"
                            letterSpacing="wider"
                            py={5}
                            px={6}
                            borderBottom="3px solid"
                            borderColor="blue.500"
                            position="relative"
                            width="48px"
                          >
                            <Checkbox
                              isChecked={allSelected}
                              isIndeterminate={isIndeterminate}
                              onChange={(e) => {
                                e.stopPropagation();
                                if (onSelectAll) onSelectAll(e.target.checked);
                              }}
                              aria-label="Select all"
                            />
                          </Th>
                        )}
                        {columns?.map((col, index) => (
                          <Th
                            key={index}
                            fontSize="13px"
                            fontWeight="800"
                            color="gray.800"
                            textTransform="uppercase"
                            letterSpacing="wider"
                            py={5}
                            px={6}
                            borderBottom="3px solid"
                            borderColor="blue.500"
                            position="relative"
                            onClick={() => col.sortable && col.onSort?.()}
                            _hover={{
                              bg: "rgba(59, 130, 246, 0.1)",
                              color: "blue.800",
                              cursor: col.sortable ? "pointer" : "default"
                            }}
                            transition="all 0.3s ease"
                            _after={{
                              content: '""',
                              position: "absolute",
                              bottom: "-3px",
                              left: "50%",
                              transform: "translateX(-50%) scaleX(0)",
                              width: "80%",
                              height: "3px",
                              bg: "linear-gradient(90deg, #3b82f6, #8b5cf6)",
                              borderRadius: "full",
                              transition: "transform 0.3s ease"
                            }}
                            _hover_after={{
                              transform: "translateX(-50%) scaleX(1)"
                            }}
                          >
                            {col.label}
                          </Th>
                        ))}
                        {!disableRowClick && !hideActions && (
                          <Th
                            fontSize="13px"
                            fontWeight="800"
                            color="gray.800"
                            textTransform="uppercase"
                            letterSpacing="wider"
                            py={5}
                            px={6}
                            borderBottom="3px solid"
                            borderColor="blue.500"
                            position="relative"
                            textAlign="center"
                            width="120px"
                            _hover={{
                              bg: "rgba(59, 130, 246, 0.1)",
                              color: "blue.800",
                              cursor: "default"
                            }}
                            transition="all 0.3s ease"
                          >
                            {common.ACTIONS}
                          </Th>
                        )}
                      </>
                    ) : (
                      <>
                        {selectable && (
                          <Th
                            fontSize="13px"
                            fontWeight="800"
                            color="gray.800"
                            textTransform="uppercase"
                            letterSpacing="wider"
                            py={5}
                            px={6}
                            borderBottom="3px solid"
                            borderColor="blue.500"
                            position="relative"
                            width="48px"
                          >
                            <Checkbox
                              isChecked={allSelected}
                              isIndeterminate={isIndeterminate}
                              onChange={(e) => {
                                e.stopPropagation();
                                if (onSelectAll) onSelectAll(e.target.checked);
                              }}
                              aria-label="Select all"
                            />
                          </Th>
                        )}
                        <Th
                          fontSize="13px"
                          fontWeight="800"
                          color="gray.800"
                          textTransform="uppercase"
                          letterSpacing="wider"
                          py={5}
                          px={6}
                          borderBottom="3px solid"
                          borderColor="blue.500"
                          position="relative"
                          _hover={{
                            bg: "rgba(59, 130, 246, 0.1)",
                            color: "blue.800",
                            cursor: "pointer"
                          }}
                          transition="all 0.3s ease"
                          _after={{
                            content: '""',
                            position: "absolute",
                            bottom: "-3px",
                            left: "50%",
                            transform: "translateX(-50%) scaleX(0)",
                            width: "80%",
                            height: "3px",
                            bg: "linear-gradient(90deg, #3b82f6, #8b5cf6)",
                            borderRadius: "full",
                            transition: "transform 0.3s ease"
                          }}
                          _hover_after={{
                            transform: "translateX(-50%) scaleX(1)"
                          }}
                        >
                          Title
                        </Th>
                        <Th
                          fontSize="13px"
                          fontWeight="800"
                          color="gray.800"
                          textTransform="uppercase"
                          letterSpacing="wider"
                          py={5}
                          px={6}
                          borderBottom="3px solid"
                          borderColor="blue.500"
                          position="relative"
                          _hover={{
                            bg: "rgba(59, 130, 246, 0.1)",
                            color: "blue.800",
                            cursor: "pointer"
                          }}
                          transition="all 0.3s ease"
                          _after={{
                            content: '""',
                            position: "absolute",
                            bottom: "-3px",
                            left: "50%",
                            transform: "translateX(-50%) scaleX(0)",
                            width: "80%",
                            height: "3px",
                            bg: "linear-gradient(90deg, #3b82f6, #8b5cf6)",
                            borderRadius: "full",
                            transition: "transform 0.3s ease"
                          }}
                          _hover_after={{
                            transform: "translateX(-50%) scaleX(1)"
                          }}
                        >
                          Year
                        </Th>
                        <Th
                          fontSize="13px"
                          fontWeight="800"
                          color="gray.800"
                          textTransform="uppercase"
                          letterSpacing="wider"
                          py={5}
                          px={6}
                          borderBottom="3px solid"
                          borderColor="blue.500"
                          position="relative"
                          _hover={{
                            bg: "rgba(59, 130, 246, 0.1)",
                            color: "blue.800",
                            cursor: "pointer"
                          }}
                          transition="all 0.3s ease"
                          _after={{
                            content: '""',
                            position: "absolute",
                            bottom: "-3px",
                            left: "50%",
                            transform: "translateX(-50%) scaleX(0)",
                            width: "80%",
                            height: "3px",
                            bg: "linear-gradient(90deg, #3b82f6, #8b5cf6)",
                            borderRadius: "full",
                            transition: "transform 0.3s ease"
                          }}
                          _hover_after={{
                            transform: "translateX(-50%) scaleX(1)"
                          }}
                        >
                          Image
                        </Th>
                        {!hideActions && (
                          <Th
                            fontSize="13px"
                            fontWeight="800"
                            color="gray.800"
                            textTransform="uppercase"
                            letterSpacing="wider"
                            py={5}
                            px={6}
                            borderBottom="3px solid"
                            borderColor="blue.500"
                            position="relative"
                            textAlign="center"
                            width="120px"
                          >
                            Actions
                          </Th>
                        )}
                      </>
                    )}
                  </Tr>
                </Thead>
                <Tbody>
                  {loading ? (
                    <Tr>
                      <Td
                        colSpan={(columns?.length || 3) + (selectable ? 2 : 1)}
                        textAlign="center"
                        py={12}
                        px={6}
                      >
                        <Box display="flex" mt="50px" flexDirection="column" alignItems="center" gap={3}>
                          <Spinner size="lg" color="blue.500" thickness="3px" />
                          <Text color="gray.500" fontSize="md" fontWeight="500">
                            {common.LOADING_DATA}
                          </Text>
                        </Box>
                      </Td>
                    </Tr>
                  ) : records?.length > 0 ? (
                    records?.map((record, rowIndex) => {
                      const rowHref = !disableRowClick ? computeHref(record) : undefined;
                      // Stretched anchor overlay per data cell so native open-in-new-tab
                      // works (right-click menu, Ctrl/Cmd+click, middle click) — mirrors
                      // GridCard/ListView. Plain left-click is intercepted and routed
                      // through SPA navigation; modified/middle clicks fall through to
                      // the browser's default new-tab/window behaviour.
                      const handleAnchorClick = (e) => {
                        if (e.defaultPrevented) return;
                        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
                        e.preventDefault();
                        navigate(rowHref);
                      };
                      const hrefOverlay = rowHref && (
                        <Box
                          as="a"
                          href={rowHref}
                          onClick={handleAnchorClick}
                          position="absolute"
                          inset={0}
                          zIndex={0}
                          data-allow-context-menu="true"
                        />
                      );
                      return (
                      <Tr
                        key={rowIndex}
                        onClick={!disableRowClick && !rowHref ? () => handleEdit(record) : undefined}
                        _hover={{
                          cursor: "pointer",
                          bg: "blue.50",
                          transform: "translateY(-1px)",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.05)"
                        }}
                        transition="all 0.2s ease"
                        borderBottom="1px solid"
                        borderColor="gray.100"
                      >
                        {columns?.length > 0 ? (
                          <>
                            {selectable && (
                              <Td
                                fontSize="14px"
                                fontWeight="400"
                                color="gray.800"
                                py={4}
                                px={6}
                                borderBottom="none"
                                width="48px"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Checkbox
                                  isChecked={selectedRowKeys?.includes(computeRowKey(record))}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    if (onRowSelect) onRowSelect(computeRowKey(record), e.target.checked, record);
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                  aria-label="Select row"
                                />
                              </Td>
                            )}
                            {columns?.map((col, colIndex) => (
                              <Td
                                key={colIndex}
                                position="relative"
                                fontSize="14px"
                                fontWeight="400"
                                color="gray.800"
                                whiteSpace="normal"
                                py={4}
                                px={6}
                                borderBottom="none"
                              >
                                {hrefOverlay}
                                <Box position="relative" zIndex={1}>
                                {
                                  col.render
                                    ? col.render(record[col.accessor], record)
                                    : (col.accessor === 'direct_url_of_preview_image' || col.accessor === 'direct_url_of_cover_image') && record[col.accessor]
                                      ? <Box display="flex" justifyContent="center">
                                        <Image
                                          src={getImageURL(record[col.accessor], "200", "100")}
                                          width="64px"
                                          height="64px"
                                          objectFit="cover"
                                          fallbackSrc={placeholderImg}
                                          alt="Preview image"
                                          borderRadius="lg"
                                          border="2px solid"
                                          borderColor="gray.200"
                                          boxShadow="sm"
                                        />
                                      </Box>
                                      : (col.accessor === 'startDate' || col.accessor === 'endDate') && record[col.accessor]
                                        ? formatDateTime(record[col.accessor])
                                        : col.accessor === 'isVisible'
                                          ? <Box
                                            display="inline-flex"
                                            alignItems="center"
                                            px={2}
                                            py={1}
                                            borderRadius="full"
                                            fontSize="xs"
                                            fontWeight="600"
                                            bg={record[col.accessor] === true ? "green.100" : "red.100"}
                                            color={record[col.accessor] === true ? "green.800" : "red.800"}
                                            border="1px solid"
                                            borderColor={record[col.accessor] === true ? "green.200" : "red.200"}
                                          >
                                            {record[col.accessor] === true ? "Yes" : "No"}
                                          </Box>
                                          : record[col.accessor] || <Text color="gray.400" fontStyle="italic">—</Text>}
                                </Box>
                              </Td>
                            ))}
                            {!disableRowClick && !hideActions && (
                              <Td
                                fontSize="15px"
                                fontWeight="500"
                                color="gray.800"
                                py={4}
                                px={6}
                                borderBottom="none"
                                textAlign="center"
                              >
                                <HStack spacing={2} justifyContent="center">
                                  <IconButton
                                    aria-label="Edit record"
                                    icon={<EditIcon />}
                                    size="md"
                                    variant="ghost"
                                    colorScheme="green"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleEdit(record);
                                    }}
                                    _hover={{
                                      bg: "green.100",
                                      transform: "scale(1.1)"
                                    }}
                                    transition="all 0.2s ease"
                                  />
                                  {onRowExport && (
                                    <IconButton
                                      aria-label="Export record"
                                      icon={<DownloadIcon />}
                                      size="md"
                                      variant="ghost"
                                      colorScheme="blue"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        onRowExport(record);
                                      }}
                                      _hover={{
                                        bg: "blue.100",
                                        transform: "scale(1.1)"
                                      }}
                                      transition="all 0.2s ease"
                                    />
                                  )}
                                  {onRowDelete && (
                                    <IconButton
                                      aria-label="Delete record"
                                      icon={<DeleteIcon />}
                                      size="md"
                                      variant="ghost"
                                      colorScheme="red"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        onRowDelete(computeRowKey(record), record);
                                      }}
                                      _hover={{
                                        bg: "red.100",
                                        transform: "scale(1.1)"
                                      }}
                                      transition="all 0.2s ease"
                                    />
                                  )}
                                </HStack>
                              </Td>
                            )}
                          </>
                        ) : (
                          <>
                            {record?.title && (
                              <Td
                                position="relative"
                                fontSize="14px"
                                fontWeight="400"
                                color="gray.800"
                                width="700px"
                                whiteSpace="normal"
                                py={4}
                                px={6}
                                borderBottom="none"
                              >
                                {hrefOverlay}
                                <Box position="relative" zIndex={1}>{record?.title}</Box>
                              </Td>
                            )}
                            {record?.sortable_year && (
                              <Td
                                position="relative"
                                fontSize="14px"
                                fontWeight="400"
                                color="gray.800"
                                py={4}
                                px={6}
                                borderBottom="none"
                              >
                                {hrefOverlay}
                                <Box position="relative" zIndex={1}>{record?.sortable_year}</Box>
                              </Td>
                            )}
                            {(record?.direct_url_of_cover_image || record?.direct_url_of_preview_image) && (
                              <Td
                                position="relative"
                                fontSize="14px"
                                fontWeight="400"
                                color="gray.800"
                                py={4}
                                px={6}
                                borderBottom="none"
                              >
                                {hrefOverlay}
                                <Box position="relative" zIndex={1} display="flex" justifyContent="center">
                                  <Image
                                    src={getImageURL(record?.direct_url_of_cover_image || record?.direct_url_of_preview_image, "200", "100")}
                                    width="120px"
                                    height="80px"
                                    objectFit="cover"
                                    fallbackSrc={placeholderImg}
                                    alt="Cover image"
                                    borderRadius="lg"
                                    border="2px solid"
                                    borderColor="gray.200"
                                    boxShadow="sm"
                                  />
                                </Box>
                              </Td>
                            )}
                            <Td
                              fontSize="15px"
                              fontWeight="500"
                              color="gray.800"
                              py={4}
                              px={6}
                              borderBottom="none"
                              textAlign="center"
                            >
                              <HStack spacing={2} justifyContent="center">
                                <IconButton
                                  aria-label="Edit record"
                                  icon={<EditIcon />}
                                  size="sm"
                                  variant="ghost"
                                  colorScheme="green"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEdit(record);
                                  }}
                                  _hover={{
                                    bg: "green.100",
                                    transform: "scale(1.1)"
                                  }}
                                  transition="all 0.2s ease"
                                />
                                {onRowExport && (
                                  <IconButton
                                    aria-label="Export record"
                                    icon={<DownloadIcon />}
                                    size="sm"
                                    variant="ghost"
                                    colorScheme="blue"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onRowExport(record);
                                    }}
                                    _hover={{
                                      bg: "blue.100",
                                      transform: "scale(1.1)"
                                    }}
                                    transition="all 0.2s ease"
                                  />
                                )}
                              </HStack>
                            </Td>
                          </>
                        )}
                      </Tr>
                      );
                    })
                  ) : (
                    <Tr>
                      <Td
                        colSpan={(columns?.length || 3) + 1}
                        textAlign="center"
                        py={12}
                        px={6}
                      >
                        <Box display="flex" flexDirection="column" alignItems="center" gap={3}>
                          <Box
                            w={16}
                            h={16}
                            bg="gray.100"
                            borderRadius="full"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                          >
                            <Text fontSize="2xl" color="gray.400">📋</Text>
                          </Box>
                          <Box textAlign="center">
                            <Text color="gray.600" fontSize="lg" fontWeight="600" mb={1}>
                              {common.NO_DATA}
                            </Text>
                            <Text color="gray.500" fontSize="sm">
                              Try adjusting your search criteria or filters
                            </Text>
                          </Box>
                        </Box>
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </TableContainer>

            {pagination && totalPages > 0 && (
              <Flex
                justifyContent="center"
                alignItems="center"
                p={4}
                bg="gray.50"
                borderTop="1px solid"
                borderColor="gray.200"
                gap={3}
                flexShrink={0}
              >
                <Button
                  size="sm"
                  onClick={() => handlePageChange(Number(currentPage - 1))}
                  isDisabled={currentPage === 1}
                  leftIcon={<ChevronLeftIcon />}
                  variant="outline"
                >
                  {common.PREVIOUS}
                </Button>

                <Flex bg="white" p={1} borderRadius="lg" border="1px solid" borderColor="gray.200" alignItems="center">
                  <Text fontSize="13px" fontWeight="600" ml={2}>{common.PAGE}</Text>
                  <Select
                    size="sm"
                    width="60px"
                    value={currentPage}
                    onChange={(e) => handlePageChange(Number(e.target.value))}
                    variant="unstyled"
                    fontWeight="700"
                    color="blue.600"
                    textAlign="center"
                  >
                    {totalPages && Array.from({ length: totalPages }, (_, i) => (
                      <option key={i + 1} value={i + 1}>{i + 1}</option>
                    ))}
                  </Select>
                  <Text fontSize="13px" fontWeight="500" mr={2}>of {totalPages}</Text>
                </Flex>

                <Button
                  size="sm"
                  onClick={() => handlePageChange(Number(currentPage + 1))}
                  isDisabled={currentPage === totalPages}
                  rightIcon={<ChevronRightIcon />}
                  variant="outline"
                >
                  {common.NEXT}
                </Button>
              </Flex>
            )}
          </Box>
          {filterPanel}
          </HStack>
        </Box>
      ) : (
        <Text>{userProfile.LOGIN_INSTRUCTION}</Text>
      )}

      {/* Global Scroll to Top Button */}
      {showScrollTop && (
        <Tooltip label="Scroll to Top" placement="left" hasArrow>
          <Button
            position="fixed"
            bottom={6}
            right={6}
            size="md"
            borderRadius="full"
            bg="linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)"
            color="white"
            boxShadow="lg"
            zIndex={1500}
            _hover={{
              transform: "translateY(-2px) scale(1.05)",
              boxShadow: "xl",
              filter: "brightness(1.1)"
            }}
            _active={{
              transform: "translateY(0) scale(1.02)"
            }}
            onClick={scrollToTop}
            transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
            minW="52px"
            h="52px"
            p={0}
            border="2px solid"
            borderColor="white"
            _before={{
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bg: "linear-gradient(135deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0) 100%)",
              borderRadius: "full",
              pointerEvents: "none"
            }}
          >
            <ChevronUpIcon boxSize={6} />
          </Button>
        </Tooltip>
      )}
    </>
  )
}

export default TableStructure;